import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';

import Accordian from 'atoms/Accordian/Accordian';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import {
  DISPOSITION_KEYS,
  SHARED_CONSTANTS,
  PAYMENT_TENDER_TYPES,
  DATE_FORMATS,
  CHECKOUT_CONSTANTS,
} from 'common/constants/SharedConstants';
import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';
import { parseInvalidDateAndFormat, getFormattedFromUnixWithTimeZone } from 'common/utilities/dayJsUtils';
import {
  getAvailableTimingsBasedOnCurrentDay,
  getOpenTime,
  getCloseTime,
  getFirstLetterUpperCase,
} from 'common/utilities/utils';
import {
  displayListOfCartItemInAccordion,
  getAddHopeDataPanel,
  getAdditionalChargesPanel,
  getCarryBagPanel,
  getCartDiscountTotalPanel,
  getCartGSTPanel,
  getAllTaxDetails,
  getCartSubTotalPanel,
  getCartTotalPanel,
  getDiscountAmountsPanel,
  getDeliveryTipPanel,
  getBottleDepositCharge,
} from 'common/sharedTemplates/sharedTemplates';
import { isOrderProcessingStatus } from 'common/utilities/orderStatusUtils';

/*********************************************************************
 * getOrderIdPanelJSX
 *
 *********************************************************************/
const getOrderIdPanelJSX = orderDetails => {
  const { displayOrderId } = orderDetails;
  return (
    <span className='orderId col-8'>
      <span className='OrderClass'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDER_HASH)}</span>
      {displayOrderId}
    </span>
  );
};

/*********************************************************************
 * getOrderDateTimePanelJSX
 *
 *********************************************************************/
export const getOrderDateTimePanelJSX = orderDetails => {
  const {
    enableClickCollectScheduleOrder,
    allowScheduleOrderForDispositions,
    service,
    enableScheduleOrder,
    requestedDateTime,
    fulfillmentDateTime,
  } = orderDetails;
  const enableClickCollectScheduleOrderFlag =
    enableClickCollectScheduleOrder && allowScheduleOrderForDispositions.includes(service);
  const orderReadyDateTime = getFormattedFromUnixWithTimeZone(
    fulfillmentDateTime,
    orderDetails?.store?.timeZoneIANA,
    DATE_FORMATS?.MMMM_D_HH_MM_A,
  );
  const isScheduledDateTime = enableScheduleOrder ? !requestedDateTime?.isForNow : false;
  const showOrderingDateTime = enableClickCollectScheduleOrderFlag || isScheduledDateTime;
  const dateTimeArray = orderReadyDateTime.split(' ');
  return showOrderingDateTime ? (
    <>
      <h2 className='order-scheduled'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.SCHEDULED)?.toUpperCase()}</h2>
      <div className='orderingDateTime'>
        {translateWithI18Next(dateTimeArray[0]) +
          ' ' +
          dateTimeArray[1] +
          ' ' +
          dateTimeArray[2] +
          ' ' +
          dateTimeArray[3]}
      </div>
    </>
  ) : null;
};
/*********************************************************************
 * getPaymentPanelJSX
 *
 *********************************************************************/
const getPaymentPanelJSX = orderDetails => {
  const { paymentType, tenders, paymentReferences } = orderDetails;
  return (
    <div className='postorder-payment-details'>
      <h2 className='payment-header'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.PAYMENT_HEADER)}</h2>
      <div className='payment-details'>
        {paymentType?.fields?.displayFormat?.toLowerCase() === SHARED_CONSTANTS?.ONLY_IMAGE?.toLowerCase() ? (
          <>
            <img src={paymentType?.fields?.icon?.fields?.desktopImage?.fields?.file?.url} alt='paymentMethod' />
            {paymentReferences?.length > 0 &&
              PAYMENT_TENDER_TYPES?.CREDIT_CARD?.toLowerCase() === tenders?.[0]?.type?.toLowerCase() && (
                <span className='payment-completed'>{` x - ${paymentReferences?.[0]?.lastFour}`}</span>
              )}
          </>
        ) : (
          <span className='payment-completed'>
            {translateWithI18Next((paymentType && paymentType?.fields?.displayName) || tenders?.[0]?.type)}
          </span>
        )}
      </div>
    </div>
  );
};

/*********************************************************************
 * getStoreDirectionPanelJSX
 *
 *********************************************************************/

export const getStoreDirectionPanelJSX = params => {
  return (
    <span
      class={`get-direction-panel ${
        params?.orderDetails?.enableRetryCountForPickupOrderTracking && 'orderdirection-icon'
      }`}
      data-testid='get-direction-panel'
    >
      <img
        src={IMAGE_PATH?.Direction}
        alt=''
        className={`get-direction-icon ${
          params?.orderDetails?.showStoreRegistrationNumber ? 'get-direction-icon-jp' : ''
        }`}
      />
      <span
        data-testid='get-direction'
        tabIndex='0'
        className={`get-direction-text ${
          params?.orderDetails?.showStoreRegistrationNumber ? 'get-direction-text-jp' : ''
        }`}
        onClick={() => params?.updateSetOrderDetails({ isOpenOrderSummaryDirectionMap: true })}
      >
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.GET_DIRECTION)}
      </span>
    </span>
  );
};

/*********************************************************************
 * getStoreHoursPanelJSX
 *
 *********************************************************************/
const getStoreHoursPanelJSX = orderDetails => {
  const { storeOperatingTime, operatingTimeFromStoreReducer } = orderDetails;
  const availableTimings = getAvailableTimingsBasedOnCurrentDay(
    storeOperatingTime?.operatingHours || operatingTimeFromStoreReducer,
  );
  var openTimeoutputFormat = '';
  var closeTimeOutputFormat = '';
  if (getOpenTime(availableTimings).split(':')[1] === '00') {
    openTimeoutputFormat = SHARED_CONSTANTS?.H_a;
  } else {
    openTimeoutputFormat = SHARED_CONSTANTS?.H_MM_a;
  }
  const fromTime = parseInvalidDateAndFormat(
    getOpenTime(availableTimings),
    SHARED_CONSTANTS?.HH_MM,
    openTimeoutputFormat,
  );

  if (getCloseTime(availableTimings).split(':')[1] === '00') {
    closeTimeOutputFormat = SHARED_CONSTANTS?.H_MM_a;
  } else {
    closeTimeOutputFormat = SHARED_CONSTANTS?.HH_MM_A;
  }
  const toTime = parseInvalidDateAndFormat(
    getCloseTime(availableTimings),
    SHARED_CONSTANTS?.HH_MM,
    closeTimeOutputFormat,
  );

  return (
    availableTimings?.length && (
      <span className='hrs-view mob-display'>{`${translateWithI18Next(
        TRANSLATE_MAPPING_KEY?.HOURS,
      )} ${fromTime}-${toTime}`}</span>
    )
  );
};

/*********************************************************************
 * orderInfoDetailsJSX
 *
 *********************************************************************/
export const orderInfoDetailsJSX = orderDetails => {
  const {
    service,
    additionalCharges,
    subTotal,
    taxLines,
    inclusiveTaxTotal,
    discountTotal,
    discountLines,
    paymentDiscountLines,
    currencySymbol,
    currencyConversionFactor,
    showCombinedDiscount,
    showTaxLines,
    postOrderDetails,
    bottleDepositChargeTotal,
    showGiftCardOrVoucherSummary,
    showPromosSummary,
    showTaxExcluded,
    paymentDiscountTotal,
    taxAmountRoundOff,
  } = orderDetails;
  const discountSubTotal = discountTotal || paymentDiscountTotal;
  return (
    <>
      <div className='line'>{displayListOfCartItemInAccordion(orderDetails)}</div>
      {Boolean(
        showGiftCardOrVoucherSummary &&
          paymentDiscountLines?.length > 0 &&
          paymentDiscountTotal > 0 &&
          !showCombinedDiscount,
      ) && (
        <>
          {getDiscountAmountsPanel({
            discountLines: paymentDiscountLines,
            currencySymbol,
            currencyConversionFactor,
            showDiscountCouponCode: true,
            showDiscountRemoveIcon: false,
            isPaymentDiscount: true,
          })}
          <div class='hr-giftcard-type'></div>
        </>
      )}
      <div
        className={
          (showPromosSummary && discountLines?.length > 0 && discountTotal > 0) || additionalCharges?.length > 0
            ? 'bottom-line'
            : ''
        }
      >
        {/* Display sub total of selected/added items in the cart */}
        {getCartSubTotalPanel(subTotal, currencySymbol, currencyConversionFactor)}

        {showTaxLines && taxLines?.length > 0
          ? getAllTaxDetails(
              taxLines,
              currencySymbol,
              currencyConversionFactor,
              CHECKOUT_CONSTANTS.CHECK_OUT_PAGE,
              taxAmountRoundOff,
            )
          : getCartGSTPanel(inclusiveTaxTotal, currencySymbol, currencyConversionFactor, showTaxExcluded)}
      </div>

      {/* Display Discount amount of selected/added items in the cart */}
      {showPromosSummary &&
        discountTotal > 0 &&
        !showCombinedDiscount &&
        getDiscountAmountsPanel({
          discountLines,
          currencySymbol,
          currencyConversionFactor,
          showDiscountCouponCode: false,
          showDiscountRemoveIcon: false,
          isPaymentDiscount: false,
        })}
      {/* Display discount total in the cart */}
      {discountSubTotal > 0 &&
        showCombinedDiscount &&
        getCartDiscountTotalPanel(discountSubTotal, currencySymbol, currencyConversionFactor)}
      {getAdditionalChargesPanel(additionalCharges, currencySymbol, currencyConversionFactor, service)}
      {/* Display Delivery tip */}
      {postOrderDetails?.driverTippingCharge > 0 &&
        getDeliveryTipPanel(postOrderDetails?.driverTippingCharge, currencySymbol, currencyConversionFactor)}

      {getBottleDepositCharge(bottleDepositChargeTotal, currencySymbol, currencyConversionFactor)}
    </>
  );
};

/*********************************************************************
 * getDeliveryAddressPanelJSX
 *
 *********************************************************************/
const getDeliveryAddressPanelJSX = orderDetails => {
  const { fullAddressDetails } = orderDetails;
  return (
    <>
      <h2 className='orderFrom'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.DELIVERY_ADDRESS)}</h2>
      <div className='place'>{fullAddressDetails}</div>
    </>
  );
};

/*********************************************************************
 * getPreparedByPanelJSX
 *
 *********************************************************************/
export const getPreparedByPanelJSX = params => {
  const { orderDetails } = params;
  const {
    showStoreDirectinPanelInOrderSummaryInfoDetails,
    showStoreHoursPanelInOrderSummaryInfoDetails,
    store,
    showStoreRegistrationNumber,
    showOrderDateTime,
    showOrderMode,
    showOrderStatus,
    showPickupOrDeliveryTime,
  } = orderDetails;
  return (
    <>
      <h2 className='orderFrom'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.PREPARED_BY)}</h2>
      <div className='preparedByPanel place'>
        <div className='place-direction'>
          <span className='fnt-bld mob-display storeName'>{orderDetails?.storeName}</span>
          {showStoreDirectinPanelInOrderSummaryInfoDetails && getStoreDirectionPanelJSX(params)}
          <div>{showStoreRegistrationNumber && getRegistrationPanelJsx(orderDetails)}</div>
        </div>
        {showStoreHoursPanelInOrderSummaryInfoDetails && getStoreHoursPanelJSX(orderDetails)}
      </div>
      {orderDetails?.enableRetryCountForPickupOrderTracking && (
        <div className='phone-details'>
          <img src={IMAGE_PATH?.phoneImg} alt='' className='get-direction-icon' />
          <a tabIndex='-1' href={'tel:' + store?.phoneNumber} className='ml-2 phone-details-text'>
            {store?.phoneNumber}
          </a>
        </div>
      )}
      {showOrderDateTime && getDateTimePanelJsx(orderDetails)}
      {showOrderMode && getOrderModePanelJsx(orderDetails)}
      {showOrderStatus && getOrderStatusPanelJsx(orderDetails)}
      {showPickupOrDeliveryTime && orderDetails?.service === 'pickup'
        ? getPickupDateTimePanelJsx(orderDetails)
        : getDeliveryDateTimePanelJsx(orderDetails)}
    </>
  );
};

/*********************************************************************
 * getInstructionsPanelJSX
 *
 *********************************************************************/
const getInstructionsPanelJSX = orderDetails => {
  const { instruction } = orderDetails;

  return (
    instruction && (
      <>
        <h2 className='orderFrom'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.INSTRUCTIONS)?.toUpperCase()}</h2>
        <div className='place'>
          <span className='mob-display'>{instruction}</span>
        </div>
      </>
    )
  );
};

/*********************************************************************
 * deliveryViewPanel
 *
 *********************************************************************/
export const deliveryViewPanel = params => {
  const { orderDetails } = params;
  const { total, currencySymbol, currencyConversionFactor, orderSummaryInfoDetailsShowInAccordion, service } =
    orderDetails;

  return (
    <div className={`order-processing-info-container ${service}`}>
      {!orderSummaryInfoDetailsShowInAccordion && getOrderIdPanelJSX(orderDetails)}
      {getOrderDateTimePanelJSX(orderDetails)}
      {getDeliveryAddressPanelJSX(orderDetails)}

      {getInstructionsPanelJSX(orderDetails)}

      {getPreparedByPanelJSX(params)}

      {orderInfoDetailsJSX(orderDetails)}
      <div class='hr-payment-type'></div>
      {getCartTotalPanel(total, currencySymbol, currencyConversionFactor, TRANSLATE_MAPPING_KEY?.TOTAL_GST_INCLUDED)}

      <div class='hr-payment-type'></div>
      {getPaymentPanelJSX(orderDetails)}
    </div>
  );
};

/*********************************************************************
 * pickupViewPanel
 *
 *********************************************************************/
export const pickupViewPanel = params => {
  const { orderDetails } = params;
  const {
    addHope,
    carryBag,
    service,
    foodLines,
    total,
    currencySymbol,
    currencyConversionFactor,
    orderSummaryInfoDetailsShowInAccordion,
  } = orderDetails;
  return (
    <div className={`order-processing-info-container ${service} col-12`}>
      {!orderSummaryInfoDetailsShowInAccordion && getOrderIdPanelJSX(orderDetails)}
      {getOrderDateTimePanelJSX(orderDetails)}
      {getPreparedByPanelJSX(params)}

      {orderInfoDetailsJSX(orderDetails)}

      {/* Display Carry Bag amount added in the cart */}
      {getCarryBagPanel(service, foodLines, carryBag, currencySymbol, currencyConversionFactor)}

      {/* Display Add hope amount added in the cart */}
      {getAddHopeDataPanel(foodLines, addHope, currencySymbol, currencyConversionFactor)}
      <div class='hr-payment-type'></div>

      {getCartTotalPanel(total, currencySymbol, currencyConversionFactor)}

      <div class='hr-payment-type'></div>
      {getPaymentPanelJSX(orderDetails)}
    </div>
  );
};

/*********************************************************************
 * cateringViewPanel
 *
 *********************************************************************/
export const cateringViewPanel = params => {
  return pickupViewPanel(params);
};

/*********************************************************************
 * dineInViewPanel
 *
 *********************************************************************/
export const dineInViewPanel = params => {
  return pickupViewPanel(params);
};

/*********************************************************************
 * staticPanelView
 *
 *********************************************************************/
export const staticPanelView = params => {
  switch (params?.orderDetails?.service?.toUpperCase()) {
    case DISPOSITION_KEYS.DELIVERY:
      return deliveryViewPanel(params);
    case DISPOSITION_KEYS.PICKUP:
      return pickupViewPanel(params);
    case DISPOSITION_KEYS.DINE_IN:
      return dineInViewPanel(params);
    case DISPOSITION_KEYS.CATERING:
      return cateringViewPanel(params);
    default:
      return pickupViewPanel(params);

    // no default
  }
};

/*********************************************************************
 * toggleAccordionPanelHandler
 *
 *********************************************************************/
const toggleAccordionPanelHandler = (event, additionalDetails) => {
  const { updateSetOrderDetails, orderDetails } = additionalDetails;
  updateSetOrderDetails({
    orderSummaryInfoDetailsOpenAccordion: !orderDetails?.orderSummaryInfoDetailsOpenAccordion,
  });
  const accordion = event?.target?.parentElement?.parentElement?.parentElement;
  accordion?.classList?.contains('terms-accordian-open')
    ? accordion?.classList?.remove('terms-accordian-open')
    : accordion?.classList?.add('terms-accordian-open');
};

/*********************************************************************
 * accordionPanelView
 *
 *********************************************************************/
export const accordionPanelView = params => {
  const { orderDetails } = params;
  const { orderId, displayOrderId, orderSummaryInfoDetailsOpenAccordion } = orderDetails;
  return (
    <div className='order-processing-info-accordion col-12'>
      <div className='order-processing-info-accordion-info'>
        <div class='circle-topp'></div>
        <div className='accordion-Title'>
          <div className='order-processing-info-accordian' id={orderId}>
            <Accordian
              dataTestId='order-processing-info-container'
              cssClass='order-processing-info-container'
              header={`${translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDER_HASH)}${displayOrderId}`}
              open={orderSummaryInfoDetailsOpenAccordion}
              onClickHandler={toggleAccordionPanelHandler}
              additionalDetails={params}
            >
              <div className='order-processing-info-accordian-description'>{staticPanelView(params)}</div>
            </Accordian>
          </div>
        </div>
        <div class='circle-bottomm'></div>
      </div>
    </div>
  );
};
/*********************************************************************
 * RegistrationNumberPanelView
 *
 *********************************************************************/
export const getRegistrationPanelJsx = orderDetails => {
  const { StoreRegistrationNumber } = orderDetails;
  return (
    <div>
      {translateWithI18Next(TRANSLATE_MAPPING_KEY?.STORE_REGISTRATION_NUMBER)} : {StoreRegistrationNumber}
    </div>
  );
};
/*********************************************************************
 * OrderDateTimePanelView
 *
 *********************************************************************/
export const getDateTimePanelJsx = orderDetails => {
  const { orderingDayAndMonthName, orderingTime } = orderDetails;
  return (
    <>
      <div className='order-field-title'>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDER_DATE_TIME)} :
        <p className='order-field-value'>
          {' '}
          {orderingDayAndMonthName}, {orderingTime}
        </p>
      </div>
    </>
  );
};
/*********************************************************************
 * OrderModePanelView
 *
 *********************************************************************/
export const getOrderModePanelJsx = orderDetails => {
  const { service } = orderDetails;

  return (
    <>
      <div className='order-field-title'>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDER_MODE)} :
        <p className='order-field-value'>{getFirstLetterUpperCase(service)}</p>
      </div>
    </>
  );
};
/*********************************************************************
 * OrderStatusPanelView
 *
 *********************************************************************/
export const getOrderStatusPanelJsx = orderDetails => {
  const { orderStatus } = orderDetails;

  return (
    <>
      <div className='order-field-title'>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDER_STATUS)} :
        <p className='order-field-value'>
          {getFirstLetterUpperCase(
            translateWithI18Next(
              isOrderProcessingStatus(orderStatus)
                ? TRANSLATE_MAPPING_KEY?.PROCESSING
                : TRANSLATE_MAPPING_KEY?.CANCELED,
            ),
          )}
        </p>
      </div>
    </>
  );
};
/*********************************************************************
 * DeliveryDateTimePanelView
 *
 *********************************************************************/
export const getDeliveryDateTimePanelJsx = orderDetails => {
  const { deliveryOrEstimatedOrCollectAtDateFullMonthNameTime } = orderDetails;

  return (
    <>
      <div className='order-field-title'>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.DELIVERY_DATE_TIME)} :
        <p className='order-field-value'> {deliveryOrEstimatedOrCollectAtDateFullMonthNameTime}</p>
      </div>
    </>
  );
};
/*********************************************************************
 * DeliveryDateTimePanelView
 *
 *********************************************************************/
export const getPickupDateTimePanelJsx = orderDetails => {
  const { pickupOrderEstimatedOrCollectAtDateFullMonthNameTime } = orderDetails;

  return (
    <>
      <div className='order-field-title'>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.PICKUP_DATE_TIME)} :
        <p className='order-field-value'> {pickupOrderEstimatedOrCollectAtDateFullMonthNameTime}</p>
      </div>
    </>
  );
};
